import React, { CSSProperties } from "react";
import clsx from "clsx";
import get from "lodash.get";
import set from "lodash.set";
import { ICellRendererParams, ColDef } from "ag-grid-community";
import { FormattedViewData, isSystemCol, QUICK_VIEW_COL_ID, IParams, CorreColDef } from "./colDef";
import { isEmptyValue } from "../../utils";
import NewLineToBr from "../ui/NewLineToBr";
import { ILayoutFormat } from "../../models/grid/dataView";

type ICellParams = ICellRendererParams | { rowIndex?: number; rowData?: any[] };

// prepares data for detail view, using colDefs for formatting
export function getFormattedDataForView<T>(
	colDefs: ColDef[],
	data: T,
	onDataUpdated?: (data) => any,
	layout?: ILayoutFormat,
	cellParams?: ICellParams,
	skipEnrichCols?: boolean
): FormattedViewData {
	const formattedData: FormattedViewData = {};
	const cols: ColDef[] = colDefs;
	cols.forEach((colDef: ColDef) => {
		if (
			(!(
				(colDef as CorreColDef).ignoreInDetailView ||
				((colDef as CorreColDef).ignoreInDetailViewFn && (colDef as CorreColDef).ignoreInDetailViewFn(data))
			) &&
				!isSystemCol(colDef)) ||
			colDef.colId === QUICK_VIEW_COL_ID
		) {
			const params: IParams = getParams(colDef, data, cellParams);
			const colId: string = colDef.colId || colDef.field;
			const value: any = get(data, colDef.field);
			const formattedValue: any = _getFormattedColValue(params, layout);
			if (!get(formattedData, colId)) {
				set(formattedData, colId, {
					display: colDef.headerName,
					value,
					isEmpty: isEmptyValue(value),
					formattedValue,
					className: getClassNames(params, layout),
					style: getStyle(params),
					headerClassName: getHeaderClassNames(params),
					onClick: (colDef as CorreColDef).isClickActionCol
						? () =>
								colDef.onCellClicked({
									data,
									value,
									node: {
										setData: (dataWithLoadData) => {
											return onDataUpdated ? onDataUpdated(dataWithLoadData) : undefined;
										},
									},
								} as any)
						: undefined,
				});
			}
		}
	});
	// console.debug("formattedData:", formattedData);
	return formattedData;
}

export const getFormattedColValue = (
	colDef: ColDef,
	data: any,
	getInnerContent?: boolean,
	ignoreRenderers?: boolean,
	layout?: ILayoutFormat,
	cellParams?: ICellParams
): any => {
	if (!data) return undefined;
	const params: any = getParams(colDef, data, cellParams);
	return _getFormattedColValue(params, layout, getInnerContent, ignoreRenderers);
};

const _getFormattedColValue = (
	params: IParams,
	layout?: ILayoutFormat,
	getInnerContent?: boolean,
	ignoreRenderers?: boolean
) => {
	const { data, colDef } = params;
	if (!data) return undefined;
	let fieldData = get(data, colDef.field);
	let renderer: any = ignoreRenderers
		? colDef.valueFormatter || colDef.valueGetter
		: colDef.cellRendererSelector ||
		  colDef.cellRendererFramework ||
		  colDef.cellRenderer ||
		  colDef.valueFormatter ||
		  colDef.valueGetter;
	if (layout === "list" && (colDef as CorreColDef).listViewRenderer) {
		renderer = (colDef as CorreColDef).listViewRenderer;
	}
	if (renderer && typeof renderer === "function") {
		let view = renderer(params);
		if (colDef.cellRendererSelector) {
			if (view && view.component) {
				view = getRendererSelectorComponent(view.component, params);
			}
		}
		if (!ignoreRenderers && getInnerContent && view && view.props && view.props.children)
			return view.props.children;

		return view;
	}
	return fieldData;
};

const getParams = (colDef: ColDef, data: any, cellParams?: ICellParams): IParams => {
	if (!data) return undefined;
	let fieldData = get(data, colDef.field);
	const params: IParams = {
		...cellParams,
		value: fieldData,
		data,
		colDef,
		getValue: (field: string) => data[field],
	};
	if (colDef.valueGetter) {
		if (typeof colDef.valueGetter === "string") {
			fieldData = colDef.valueGetter;
		} else {
			fieldData = colDef.valueGetter(params as any);
		}
		params.value = fieldData;
	}
	return params;
};

const getStyle = (params: IParams): CSSProperties => {
	const colDef: CorreColDef = params.colDef as CorreColDef;
	let cellStyle = colDef.cellStyle;
	if (typeof cellStyle === "function") {
		cellStyle = cellStyle(params as any);
	}
	return cellStyle as CSSProperties;
};

const getClassNames = (params: IParams, layout: ILayoutFormat): string => {
	const classes = [];
	const { colDef } = params;
	for (let c in colDef.cellClassRules) {
		const predicate = colDef.cellClassRules[c];
		let addClass: boolean = true;
		if (predicate && typeof predicate === "function") {
			addClass = predicate(params);
		}
		if (addClass) classes.push(c);
	}
	let cellClass = colDef.cellClass;
	if (typeof cellClass === "function") {
		cellClass = cellClass(params as any);
	}
	let listViewClass: string;
	if (layout && layout === "list") {
		listViewClass = (colDef as CorreColDef).listViewCellClass;
		if ((colDef as CorreColDef).listViewOmitCellClass) {
			cellClass = "";
		}
	}
	return clsx(cellClass, ...classes, layout && layout === "list" && (colDef as CorreColDef).listViewCellClass);
};

const getHeaderClassNames = (params: IParams): string => {
	const { colDef } = params;
	if (!colDef.headerClass) return undefined;
	let headerClass = colDef.headerClass;
	if (typeof headerClass === "function") {
		headerClass = headerClass(params);
	}
	if (Array.isArray(headerClass)) {
		return clsx(...headerClass);
	}
	return headerClass;
};

const getRendererSelectorComponent = (compName: string, params) => {
	switch (compName) {
		case "ProgressCellRenderer":
		// return <ProgressCellRenderer {...params} />;
		case "ErrorCellRenderer":
		case "agGroupCellRenderer":
			// return <ErrorCellRenderer {...params} />;
			// return <ProgressCellRenderer {...params} />;
			return null;
	}
};

export const getNameWithGroupDisplay = (name: string = "", group: string): string =>
	name + (!!group ? ` | ${group}` : "");

export function getRenderedStringsWithComma(arr: any[], displayField?: string): string {
	return arr.map((item: any) => `${item[displayField]}`).join(", ");
}

export function notesFormatter(data) {
	return (
		<span className="font-text">
			<NewLineToBr value={data.value} />
		</span>
	);
}
